import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ArrowPathIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DatePicker } from '../components/ui/date-picker';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tip } from '../components/ui/tip';
import { hoyLocal, toYMD } from '../utils/fechaLocal';

type ExpType = 'fijo' | 'variable';
type ExpCategory = 'estructura' | 'personal' | 'servicios' | 'impuestos' | 'otros';

interface Expense {
  id: string;
  date: string;
  concept: string;
  amount: number;
  category: ExpCategory;
  paymentMethod?: string | null;
  type: ExpType;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface Totals {
  total: number;
  totalFijo: number;
  totalVariable: number;
}

const CATEGORY_LABELS: Record<ExpCategory, string> = {
  estructura: 'Estructura',
  personal: 'Personal',
  servicios: 'Servicios',
  impuestos: 'Impuestos',
  otros: 'Otros',
};

const CATEGORY_COLORS: Record<ExpCategory, string> = {
  estructura: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  personal: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  servicios: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
  impuestos: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  otros: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
};

const fmtMoney = (n: number) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (iso: string) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const monthLabel = (d: Date) =>
  d.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });

const monthRange = (d: Date) => {
  const from = new Date(d.getFullYear(), d.getMonth(), 1);
  const to = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const fmt = (x: Date) => toYMD(x);
  return { from: fmt(from), to: fmt(to) };
};

const todayISO = () => hoyLocal();

const OperatingExpenses: React.FC = () => {
  const [items, setItems] = useState<Expense[]>([]);
  const [totals, setTotals] = useState<Totals>({ total: 0, totalFijo: 0, totalVariable: 0 });
  const [loading, setLoading] = useState(false);

  // Filtros
  const [currentMonth, setCurrentMonth] = useState<Date>(() => new Date());
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');

  // Dialog ABM
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [form, setForm] = useState<Partial<Expense>>({
    date: todayISO(),
    concept: '',
    amount: 0,
    category: 'estructura',
    type: 'fijo',
    notes: '',
    paymentMethod: 'efectivo',
  });
  const [saving, setSaving] = useState(false);

  // Dialog confirm delete
  const [delDlg, setDelDlg] = useState<Expense | null>(null);

  const range = useMemo(() => monthRange(currentMonth), [currentMonth]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { from: range.from, to: range.to };
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterType !== 'all') params.type = filterType;
      const res = await axios.get('/api/operating-expenses', { params });
      setItems(res.data.items || []);
      setTotals(res.data.totals || { total: 0, totalFijo: 0, totalVariable: 0 });
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Error al cargar gastos');
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, filterCategory, filterType]);

  useEffect(() => { loadData(); }, [loadData]);

  const openNew = () => {
    setEditing(null);
    setForm({
      date: todayISO(),
      concept: '',
      amount: 0,
      category: 'estructura',
      type: 'fijo',
      notes: '',
    });
    setDlgOpen(true);
  };

  const openEdit = (exp: Expense) => {
    setEditing(exp);
    setForm({
      date: exp.date,
      concept: exp.concept,
      amount: exp.amount,
      category: exp.category,
      type: exp.type,
      notes: exp.notes || '',
    });
    setDlgOpen(true);
  };

  const save = async () => {
    if (!form.date || !form.concept || form.amount == null || form.amount < 0) {
      toast.error('Completá fecha, concepto y monto válido');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`/api/operating-expenses/${editing.id}`, form);
        toast.success('Gasto actualizado');
      } else {
        await axios.post('/api/operating-expenses', form);
        toast.success('Gasto cargado');
      }
      setDlgOpen(false);
      loadData();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!delDlg) return;
    try {
      await axios.delete(`/api/operating-expenses/${delDlg.id}`);
      toast.success('Gasto eliminado');
      setDelDlg(null);
      loadData();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Error al eliminar');
    }
  };

  const prevMonth = () => setCurrentMonth(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setCurrentMonth(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const goToday = () => setCurrentMonth(new Date());

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <CurrencyDollarIcon className="h-8 w-8 text-emerald-600" />
            Gastos operativos
          </h1>
          <p className="text-muted-foreground mt-1">
            Carga manual de gastos que NO pasan por compras (alquiler, sueldos, servicios, impuestos).
            Los pagos a proveedores y compras de stock se registran en sus propios módulos.
          </p>
        </div>
        <Button onClick={openNew}>
          <PlusIcon className="h-4 w-4 mr-2" />
          Nuevo gasto
        </Button>
      </div>

      {/* Selector de mes + filtros */}
      <Card>
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prevMonth}><ChevronLeftIcon className="h-4 w-4"/></Button>
            <div className="px-3 py-1.5 text-sm font-semibold capitalize min-w-[180px] text-center border rounded">
              <CalendarIcon className="h-4 w-4 inline mr-1.5 -mt-0.5"/>
              {monthLabel(currentMonth)}
            </div>
            <Button variant="outline" size="sm" onClick={nextMonth}><ChevronRightIcon className="h-4 w-4"/></Button>
            <Button variant="ghost" size="sm" onClick={goToday}>Hoy</Button>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[180px]"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {Object.entries(CATEGORY_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[140px]"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Fijo + Variable</SelectItem>
                <SelectItem value="fijo">Solo Fijos</SelectItem>
                <SelectItem value="variable">Solo Variables</SelectItem>
              </SelectContent>
            </Select>
            <Tip label="Refrescar">
              <Button variant="outline" size="sm" onClick={loadData}>
                <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}/>
              </Button>
            </Tip>
          </div>
        </CardContent>
      </Card>

      {/* Totales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total del mes</div>
            <div className="text-3xl font-bold">{fmtMoney(totals.total)}</div>
            <div className="text-xs text-muted-foreground mt-1">{items.length} gasto{items.length !== 1 ? 's' : ''}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Costos fijos</div>
            <div className="text-3xl font-bold text-blue-600">{fmtMoney(totals.totalFijo)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {totals.total > 0 ? ((totals.totalFijo / totals.total) * 100).toFixed(0) : 0}% del total
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Costos variables</div>
            <div className="text-3xl font-bold text-orange-600">{fmtMoney(totals.totalVariable)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {totals.total > 0 ? ((totals.totalVariable / totals.total) * 100).toFixed(0) : 0}% del total
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[110px]">Fecha</TableHead>
                <TableHead>Concepto</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Monto</TableHead>
                <TableHead className="w-[100px] text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Cargando...</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                  <div className="flex flex-col items-center gap-2">
                    <CurrencyDollarIcon className="h-10 w-10 opacity-30"/>
                    <p>No hay gastos cargados este mes.</p>
                    <Button variant="outline" size="sm" onClick={openNew} className="mt-2">
                      <PlusIcon className="h-4 w-4 mr-1.5"/> Cargá el primero
                    </Button>
                  </div>
                </TableCell></TableRow>
              ) : (
                items.map(exp => (
                  <TableRow key={exp.id}>
                    <TableCell className="font-mono text-xs">{fmtDate(exp.date)}</TableCell>
                    <TableCell>
                      <div className="font-medium">{exp.concept}</div>
                      {exp.notes && <div className="text-xs text-muted-foreground mt-0.5">{exp.notes}</div>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={CATEGORY_COLORS[exp.category]}>
                        {CATEGORY_LABELS[exp.category]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={exp.type === 'fijo' ? 'default' : 'secondary'}>
                        {exp.type === 'fijo' ? 'Fijo' : 'Variable'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold">{fmtMoney(exp.amount)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Tip label="Editar">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(exp)}>
                            <PencilIcon className="h-4 w-4"/>
                          </Button>
                        </Tip>
                        <Tip label="Eliminar">
                          <Button variant="ghost" size="icon" onClick={() => setDelDlg(exp)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950">
                            <TrashIcon className="h-4 w-4"/>
                          </Button>
                        </Tip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog ABM */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar gasto' : 'Nuevo gasto'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Actualizá los datos del gasto.' : 'Cargá un gasto que no provenga de compras a proveedores.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="exp-date">Fecha</Label>
                <DatePicker id="exp-date" value={form.date || ''} onChange={v => setForm({ ...form, date: v })}/>
              </div>
              <div>
                <Label htmlFor="exp-amount">Monto $</Label>
                <Input id="exp-amount" type="number" min="0" step="0.01" value={form.amount ?? ''}
                       onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })}/>
              </div>
            </div>
            <div>
              <Label htmlFor="exp-concept">Concepto</Label>
              <Input id="exp-concept" placeholder="Ej: Alquiler octubre, Sueldo Juan, Luz EDESA, Monotributo"
                     value={form.concept || ''} onChange={e => setForm({ ...form, concept: e.target.value })}/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="exp-category">Categoría</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v as ExpCategory })}>
                  <SelectTrigger id="exp-category"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="exp-type">Tipo</Label>
                <Select value={form.type} onValueChange={v => setForm({ ...form, type: v as ExpType })}>
                  <SelectTrigger id="exp-type"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fijo">Fijo</SelectItem>
                    <SelectItem value="variable">Variable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="text-xs bg-muted/50 p-2 rounded text-muted-foreground">
              <strong>Fijo</strong>: gasto que se paga aunque no factures (alquiler, sueldos base, monotributo).<br/>
              <strong>Variable</strong>: gasto que escala con el negocio (comisiones, premios por venta).
            </div>
            <div>
              <Label htmlFor="exp-method">¿Con qué se pagó?</Label>
              <Select value={form.paymentMethod || 'efectivo'} onValueChange={v => setForm({ ...form, paymentMethod: v })}>
                <SelectTrigger id="exp-method"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="transferencia">Transferencia / banco</SelectItem>
                  <SelectItem value="debito">Débito</SelectItem>
                  <SelectItem value="qr">MercadoPago / QR</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Alimenta los saldos por cuenta de Finanzas → Posición.</p>
            </div>
            <div>
              <Label htmlFor="exp-notes">Notas (opcional)</Label>
              <Textarea id="exp-notes" rows={2} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving}>{saving ? 'Guardando...' : (editing ? 'Guardar' : 'Crear')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog confirm delete */}
      <Dialog open={!!delDlg} onOpenChange={o => !o && setDelDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar gasto</DialogTitle>
            <DialogDescription>
              ¿Confirmás eliminar el gasto <strong>"{delDlg?.concept}"</strong> de {fmtMoney(delDlg?.amount || 0)}?
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelDlg(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmDelete}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OperatingExpenses;
